import React, { useState, useEffect } from 'react';
import { Box, Text, Flex } from '@chakra-ui/react';
import {
  Button,
  ButtonIcon,
  FormFeedback,
  OutlineCard,
  useToast
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { IFiguresAndTablesFile } from '@publishing-engine/domain/models';
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form';
import { FormFieldTablesInput } from '@publishing-engine/domain/types/article';
import FormFieldTables from 'src/components/FormField/FormFieldTables';
import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { DeleteFigureModal } from 'src/components/Submission/GenericFieldComponents/Figures/DeleteFigureModal';
import { getValidationTypedMessage } from 'src/components/FormField/common/errorMessages';
import { FieldLabel } from 'src/components/Field';

interface TablesProps {
  config: FormFieldTablesInput;
  submissionId: string;
  isFromEditModal?: boolean;
}

export type ITablesValue = {
  fileSet: {
    Tables?: IFiguresAndTablesFile;
  };
};

const Tables: React.FC<TablesProps> = ({
  config,
  submissionId,
  isFromEditModal
}) => {
  const methods = useFormContext<ITablesValue>();

  const [showTablesForm, setShowTablesForm] = useState(false);
  const [deleteTables, setDeleteTables] = useState<boolean>(false);
  const { control } = methods;
  const toast = useToast();
  const fieldName = config.caseDataFields.main;

  // The title is the article type's label for the field, and SubmissionSidebar
  // scrolls to a section by looking that label up as a DOM id, so the label has
  // to stay the id of the heading.
  const fieldLabel = config.params.labels.main || 'Tables';
  const validations = config.params.validations;
  // A required array is given `minItems: 1` by the schema even when no minimum
  // is configured, so either one makes the field mandatory to the submitter.
  const isFieldRequired =
    Boolean(validations.required) || (validations.minItems ?? 0) > 0;

  // Tables are mandatory for some article types, and the schema reports that as
  // an error on the array itself rather than on a row. Nothing rendered it, so
  // a submission was blocked with no visible reason when the user had never
  // opened the table.
  const tablesErrors = methods.formState.errors?.fileSet?.Tables as
    | FieldError
    | FieldError[]
    | undefined;
  const tablesError =
    tablesErrors && !Array.isArray(tablesErrors) ? tablesErrors : undefined;

  // move useFieldArray to parent
  const { fields, append, move, update, remove } = useFieldArray({
    control,
    name: fieldName as any
  });

  const [selectedFigures, setSelectedFigures] = useState<
    Record<string, boolean>
  >({});

  const toggleRow = (id: string) => {
    setSelectedFigures(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleSelectAll = () => {
    const allSelected = fields.every(f => selectedFigures[f.id]);
    const updated: Record<string, boolean> = {};

    fields.forEach(f => {
      updated[f.id] = !allSelected;
    });

    setSelectedFigures(updated);
  };

  const deleteSelected = () => {
    const indexesToDelete = fields
      .map((field, index) => (selectedFigures[field.id] ? index : -1))
      .filter(i => i !== -1);
    if (indexesToDelete.length === 0) return;

    const allSelected = indexesToDelete.length === fields.length;

    if (allSelected) {
      remove([...indexesToDelete]);
      //append({ caption: '', file: null }); // add single empty row
      setSelectedFigures({});
      setDeleteTables(false);
      toast({
        variant: 'success',
        title: `${indexesToDelete.length} table${
          indexesToDelete.length > 1 ? 's' : ''
        } deleted successfully.`
      });
      setShowTablesForm(false);
      return;
    }

    // delete only selected
    remove(indexesToDelete);

    // fix selected array size
    setSelectedFigures(() => {
      const updated: Record<string, boolean> = {};
      fields.slice(0, fields.length - indexesToDelete.length).forEach(f => {
        updated[f.id] = false;
      });
      return updated;
    });

    toast({
      variant: 'success',
      title: `${indexesToDelete.length} table${
        indexesToDelete.length > 1 ? 's' : ''
      } deleted successfully.`
    });
    setDeleteTables(false);
  };
  useEffect(() => {
    const values = methods.getValues();
    if (
      Array.isArray(values?.fileSet?.Tables) &&
      values.fileSet.Tables.length > 0
    ) {
      setShowTablesForm(true);
    }
  }, [methods]);

  return (
    <OutlineCard width="100%" padding={4}>
      <Flex flexDirection="column" width="100%">
        <Flex
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <FieldLabel
            label={isFromEditModal ? `Update ${fieldLabel}` : fieldLabel}
            subText=""
            isRequired={isFieldRequired}
            textStyle={isFromEditModal ? 'headingSection' : 'headingItem'}
            helpText={config.params.labels.helpText}
          />
          <span>
            <ButtonIcon
              size="regular"
              ariaLabel="right-delete-button"
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => {
                if (!Object.values(selectedFigures).some(x => x)) {
                  toast({
                    variant: 'attention',
                    title: 'No table selected'
                  });
                  return;
                }
                setDeleteTables(true);
              }}
            />
          </span>
        </Flex>
        <Flex flexDirection="column" gap={4} paddingBottom={4}>
          {!showTablesForm ? (
            <Box textAlign="center" width="100%">
              <Text textStyle="context" mb={4}>
                Click on 'Add Tables' to add tables
              </Text>
              <Button
                variant="primary"
                onClick={() => setShowTablesForm(true)}
                gap={2}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add Tables
              </Button>
            </Box>
          ) : (
            <Box textAlign="center">
              <FormFieldTables
                config={config}
                submissionId={submissionId}
                fields={fields}
                append={append}
                remove={remove}
                selected={selectedFigures}
                toggleRow={toggleRow}
                toggleSelectAll={toggleSelectAll}
                methods={methods}
                move={move}
                update={update}
                onLastTableDeleted={() => {
                  setShowTablesForm(false);
                  setSelectedFigures({});
                }}
              />
            </Box>
          )}
        </Flex>
        {tablesError && (
          <FormFeedback variant="error">
            {getValidationTypedMessage(
              fieldLabel,
              validations.kind,
              validations,
              tablesError
            )}
          </FormFeedback>
        )}
        {deleteTables && (
          <DeleteFigureModal
            onClose={() => setDeleteTables(false)}
            isOpen={deleteTables}
            onDelete={deleteSelected}
            selectedFigures={selectedFigures}
            filedName="Table"
          />
        )}
      </Flex>
    </OutlineCard>
  );
};

export default Tables;
